import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { updateUserProfile } from "../../utils/userApi";

const ID_TYPES = [
  { value: "nin", label: "National ID (NIN)" },
  { value: "passport", label: "International Passport" },
  { value: "drivers_license", label: "Driver's License" },
];

const STATUS_META = {
  approved: {
    icon: "✅",
    title: "Identity Verified",
    tone: "identity-verification-approved",
  },
  pending: {
    icon: "🕐",
    title: "Verification Under Review",
    tone: "identity-verification-pending",
  },
  awaiting: {
    icon: "⚠️",
    title: "Action Needed — More Information Requested",
    tone: "identity-verification-action",
  },
  rejected: {
    icon: "❌",
    title: "Verification Not Approved",
    tone: "identity-verification-action",
  },
};

// Lets a user actually respond to a "more info requested" or "rejected" KYC
// review — before this existed there was NO post-registration way to
// re-upload an identity document at all, which is the real reason those
// profiles stayed stuck on Pending/Waiting indefinitely (see backend
// updateMe's identity-document-resubmission block for the other half of
// this fix).
const IdentityVerificationCard = ({ setStatusModal }) => {
  const { user, updateUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [idType, setIdType] = useState("nin");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const status = user?.userApprovalStatus;
  if (!status || !STATUS_META[status]) return null;

  const meta = STATUS_META[status];
  const needsAction = status === "awaiting" || status === "rejected";
  const note = user?.kycNote;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    try {
      setSubmitting(true);
      const res = await updateUserProfile({
        idType,
        identityDocument: file,
      });
      updateUser(res.user);
      setShowForm(false);
      setFile(null);
      setStatusModal?.({
        open: true,
        type: "success",
        message: "Document Submitted",
        subtext: "Your identity document has been re-submitted for review.",
      });
    } catch (err) {
      console.error(err);
      setStatusModal?.({
        open: true,
        type: "error",
        message: "Submission Failed",
        subtext: "There was an error submitting your document. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`account-profile-card identity-verification-card ${meta.tone}`}>
      <div className="account-profile-header">
        <h3>
          {meta.icon} {meta.title}
        </h3>
      </div>

      {note && (
        <div className="identity-verification-note">
          <strong>Message from our team:</strong>
          <p>{note}</p>
        </div>
      )}

      {needsAction && !showForm && (
        <button
          type="button"
          className="edit-btn"
          onClick={() => setShowForm(true)}
        >
          Re-submit Document
        </button>
      )}

      {needsAction && showForm && (
        <form onSubmit={handleSubmit} className="edit-form-card">
          <div className="form-group">
            <label>Document Type</label>
            <select value={idType} onChange={(e) => setIdType(e.target.value)}>
              {ID_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Upload Document</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="edit-cancel-btn"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-primary-btn"
              disabled={!file || submitting}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default IdentityVerificationCard;
